import type { Messages } from "./tr";

/** English UI strings. Typed as Messages so a missing or extra key fails typecheck. */
export const en: Messages = {
  "app.productLabel": "Forklift Tracking System",
  "app.logoAlt": "Expartise Makine",
  "app.demoBadge": "Demo · sample data",
  "app.skipToContent": "Skip to content",

  "common.loading": "Loading…",
  "common.loadError": "Could not load data.",
  "common.retry": "Try again",
  "common.empty": "—",
  "common.yes": "Yes",
  "common.no": "No",
  "common.backToOverview": "Back to overview",

  "nav.ariaLabel": "Main menu",
  "nav.openMenu": "Open menu",
  "nav.closeMenu": "Close menu",
  "nav.overview": "Overview",
  "nav.group.monitoring": "Monitoring",
  "nav.liveMap": "Live map",
  "nav.alarms": "Alarms",
  "nav.group.fleet": "Fleet",
  "nav.sites": "Sites",
  "nav.vehicles": "Vehicles",
  "nav.devices": "Devices",
  "nav.group.operators": "Operators",
  "nav.operators": "Operators",
  "nav.cards": "Cards",
  "nav.permissions": "Permissions",
  "nav.group.reports": "Reports",
  "nav.reportUsage": "Utilisation",
  "nav.reportEvents": "Impacts and events",
  "nav.reportRoutes": "Route history",
  "nav.group.admin": "Administration",
  "nav.users": "Users and roles",
  "nav.audit": "Audit log",
  "nav.settings": "Settings",

  "tenant.label": "Tenant",
  "tenant.switch": "Switch tenant",
  "tenant.listLabel": "Tenants you can access",
  "tenant.platformAdmin": "Platform admin",
  "tenant.none": "No tenant is linked to this account.",

  "role.viewer": "Viewer",
  "role.safety": "Safety officer",
  "role.fleet_manager": "Fleet manager",
  "role.admin": "Admin",

  "user.signOut": "Sign out",
  "user.menuLabel": "Signed-in user",

  "topbar.searchLabel": "Search vehicles",
  "topbar.searchPlaceholder": "Search fleet no, model or serial…",
  "topbar.language": "Language",
  "topbar.alarms": "Alarms",
  "topbar.alarmsCount": "{count} open alarms",
  "topbar.noAlarms": "No open alarms",
  "topbar.account": "Account: {name}",
  "topbar.breadcrumb": "You are here",

  "lang.tr": "TR",
  "lang.en": "EN",
  "lang.trName": "Turkish",
  "lang.enName": "English",

  "login.eyebrow": "Operator console",
  "login.title": "Welcome",
  "login.subtitle": "Sign in with your account to keep an eye on your fleet.",
  "login.email": "Email",
  "login.password": "Password",
  "login.password.show": "Show password",
  "login.password.hide": "Hide password",
  "login.submit": "Sign in",
  "login.submitting": "Signing in…",
  "login.method.legend": "Sign-in method",
  "login.method.password": "Password",
  "login.method.passwordDesc": "With your email and password",
  "login.method.code": "Email code",
  "login.method.codeDesc": "No password, a one-time code",
  "login.remember": "Remember me",
  "login.forgot": "Forgot password?",
  "login.forgot.hint": "Sign in with a code, then set a new password under My profile → Password.",
  "login.code.send": "Send code",
  "login.code.sending": "Sending…",
  "login.code.sent": "If there's an account for {email}, we've emailed it a sign-in code. The link in that email signs you in too.",
  "login.code.sentCodeOnly": "If there's an account for {email}, we've emailed it a sign-in code. Enter the code here so your session stays limited to this browser.",
  "login.code.changeEmail": "Use a different email",
  "login.code.label": "Sign-in code",
  "login.code.help": "Enter the {n}-digit code from the email.",
  "login.code.resend": "Resend code",
  "login.code.resendIn": "Resend in {s}s",
  "login.captcha.waiting": "You can continue once the security check is complete.",
  "login.captcha.loadFailed": "The security check couldn't load. Reload the page, and allow this site if you use a content blocker.",
  "login.help": "Your company's system administrator creates your account. Ask them for access.",
  "login.footerCompany": "Expartise Makine",
  "login.hero.eyebrow": "Forklift Tracking System",
  "login.hero.line1": "Your whole fleet",
  "login.hero.line2": "on one screen.",
  "login.hero.caption": "Impacts, location, checklists and battery data in one console.",
  "login.demo.summary": "Demo and sample accounts",
  "login.demo": "Continue with demo",
  "login.demoNote": "Demo mode runs on made-up sample data only — no real vehicles or people.",
  "login.demoAccounts": "Sample accounts for trying out roles (password: {password})",
  "login.demoCode": "No emails are sent in demo mode. With the email code method, sample accounts use the code {code}.",
  "login.error.required": "Enter your email and password.",
  "login.error.invalid": "Incorrect email or password.",
  "login.error.emailRequired": "Enter a valid email address.",
  "login.error.codeRequired": "Enter the {n}-digit code from the email.",
  "login.error.code": "That code is wrong or has expired. Request a new one and try again.",
  "login.error.captcha": "The security check failed. Complete it again and retry.",
  "login.error.rateLimited": "Too many attempts. Wait a minute and try again.",
  "login.error.notConfigured": "The server connection hasn't been set up yet.",
  "login.error.generic": "Sign-in failed. Please try again.",
  "login.footer": "Expartise Makine Ltd. Şti.",

  "dashboard.title": "Overview",
  "dashboard.subtitle": "Fleet summary for {tenant}",
  "dashboard.sitesTitle": "Sites",
  "dashboard.telemetryNote": "Operating hours, impacts and alarm counts will show up here once the telematics units start reporting.",
  "kpi.sites": "Sites and units",
  "kpi.sitesSub": "{count} main sites",
  "kpi.vehicles": "Vehicles",
  "kpi.vehiclesSub": "{active} active · {inactive} inactive",
  "kpi.devices": "Vehicles with a device",
  "kpi.devicesSub": "{count} awaiting a device",
  "kpi.users": "Users",
  "kpi.usersSub": "{count} admins",
  // Sprint 7b (ADR 0018): reports and the manager KPI panel.
  "dashboard.tabs": "Overview views",
  "dashboard.summaryTab": "Fleet summary",
  "nav.reportChecklist": "Checklist compliance",
  "nav.reportHeatmap": "Heatmap",
  "error.report_too_large": "Report too large: at most 18,400 vehicle-days (e.g. 92 days × 200 vehicles). Narrow the date range or the vehicles.",
  "error.export_too_large": "An export can contain at most 50,000 rows. Narrow the date range or the filters.",
  "error.invalid_report_range": "Invalid date range. Choose valid dates, at most 92 days apart (at most 24 hours for the route report).",
  "kpiPanel.title": "Manager panel",
  "kpiPanel.period": "Period",
  "kpiPanel.period.today": "Today",
  "kpiPanel.period.7d": "7 days",
  "kpiPanel.period.30d": "30 days",
  "kpiPanel.noData": "No data yet",
  "kpiPanel.noCompare": "Nothing to compare",
  "kpiPanel.vsPrevious": "vs. previous period",
  "kpiPanel.series": "Daily values, last {days} days",
  "kpiPanel.seriesSoc": "Average state of charge, last {days} days",
  "kpiPanel.utilisation": "Fleet utilisation",
  "kpiPanel.utilisationSub": "{active} of {total} connected vehicles used · basis: {basis}",
  "kpiPanel.basis.shifts": "shift hours",
  "kpiPanel.basis.24h": "the whole day",
  "kpiPanel.basis.work": "working hours",
  "kpiPanel.basis.mixed": "shift and working hours (mixed)",
  "kpiPanel.basisTip": "Utilisation basis: {basis}. Sites with shifts use shift hours; the others use the site's working hours (Mon–Fri 08–18 unless set).",
  "kpiPanel.impacts": "Impacts",
  "kpiPanel.impactsPer100h": "{value} per 100 operating hours",
  "kpiPanel.impactsNoHours": "No operating hours in this period",
  "kpiPanel.checklist": "Checklist compliance",
  "kpiPanel.checklistSub": "Missing in {missed} of {used} shifts the vehicles were used",
  "kpiPanel.noShifts": "No shifts defined",
  "kpiPanel.noShiftsSub": "Compliance is calculated once site shifts are set up.",
  "kpiPanel.faults": "Open critical faults",
  "kpiPanel.faultsSub": "{count} vehicles marked \"Maintenance required\"",
  "kpiPanel.geofence": "Zone violations",
  "kpiPanel.geofenceSub": "No-go entries {enter} · Work-area exits {exit} · Dwell {dwell}",
  "kpiPanel.battery": "Battery",
  "kpiPanel.batteryValue": "{count} vehicles low on charge",
  "kpiPanel.batterySub": "Deep discharges {deep} · average charge {avg}%",
  "kpiPanel.lift": "Overloads",
  "kpiPanel.liftSub": "Overload events reported by the lift module",
  "kpiPanel.documents": "Operator documents expiring",
  "kpiPanel.documentsSub": "Within 30 days · {expired} operators with an expired required document",
  "kpiPanel.footnote": "Times in the {tz} time zone. Usage data refreshes every minute. Click a tile to open the matching report with the same filters.",
  "kpiPanel.withoutDevice": "{count} vehicles have no device yet and are not part of utilisation.",
  "reports.sample": "Sample data",
  "reports.tz": "Days and times in the {tz} time zone.",
  "reports.rows": "{count} rows",
  "reports.totals": "Totals",
  "reports.noData": "No data yet. Nothing matches these filters.",
  "reports.truncated": "The list was cut at {max} rows. Narrow the date range or the filters.",
  "reports.km": "{km} km",
  "reports.kg": "{kg} kg",
  "reports.hours": "{h} h",
  "reports.estimated": "(estimated)",
  "reports.severity": "level {level}",
  "reports.operatorsHidden": "+{count} operators (not visible to you)",
  "reports.unknownCards": "Unknown cards: {count}",
  "reports.duration.hm": "{h} h {m} min",
  "reports.duration.m": "{m} min",
  "reports.status.open": "Open",
  "reports.status.acknowledged": "Acknowledged",
  "reports.basis.shifts": "Shifts",
  "reports.basis.24h": "Whole day",
  "reports.basis.work": "Working hours",
  "reports.basis.shiftsShort": "shifts",
  "reports.basis.24hShort": "24 h",
  "reports.basis.workShort": "work hrs",
  "reports.basis.shiftsHint": "Ignition time within shifts / shift time",
  "reports.basis.24hHint": "Ignition time / length of the day",
  "reports.basis.workHint": "Ignition time within working hours / working time",
  "reports.filter.basis": "Utilisation basis",
  "reports.basisOption.auto": "Default (shifts if defined, else working hours)",
  "reports.basisOption.shifts": "By shifts",
  "reports.basisOption.work": "By working hours",
  "reports.basisOption.workAt": "By working hours ({hours})",
  "reports.basisOption.workVaries": "By working hours (varies by site)",
  "reports.filter.workHours": "Working hours",
  "reports.basisOption.24h": "By the whole day (24 h)",
  "reports.range.tooLong": "Choose a range of at most 92 days.",
  "reports.range.reversed": "The end date cannot be before the start date.",
  "reports.range.invalid": "Choose a valid date range.",
  "reports.range.routeTooLong": "The route report covers at most 24 hours.",
  "reports.filter.label": "Report filters",
  "reports.filter.site": "Site",
  "reports.filter.allSites": "All sites",
  "reports.filter.vehicles": "Vehicles",
  "reports.filter.vehicle": "Vehicle",
  "reports.filter.allVehicles": "All vehicles",
  "reports.filter.nVehicles": "{count} vehicles selected",
  "reports.filter.clearVehicles": "Clear selection",
  "reports.filter.chooseVehicle": "Choose a vehicle",
  "reports.filter.operator": "Operator",
  "reports.filter.allOperators": "All operators",
  "reports.filter.from": "From",
  "reports.filter.to": "To",
  "reports.filter.dates": "Date range",
  "reports.filter.types": "Event types",
  "reports.filter.day": "Day",
  "reports.filter.startTime": "Start time",
  "reports.filter.endTime": "End time",
  "reports.filter.window": "Time window",
  "reports.group.impact": "Impact",
  "reports.group.safety": "Safety (seat, belt, no card, tamper)",
  "reports.group.geofence": "Zones",
  "reports.group.checklist": "Checklist",
  "reports.group.overload": "Overload",
  "reports.group.battery": "Battery",
  "reports.col.vehicle": "Vehicle",
  "reports.col.day": "Day",
  "reports.col.ignition": "Ignition on",
  "reports.col.moving": "Moving",
  "reports.col.idle": "Idle",
  "reports.col.distanceKm": "Distance (km)",
  "reports.col.lifts": "Lifts",
  "reports.col.liftedKg": "Lifted load (kg)",
  "reports.col.utilisation": "Utilisation %",
  "reports.col.basis": "Utilisation basis",
  "reports.col.operators": "Operators",
  "reports.col.operator": "Operator",
  "reports.col.time": "Time",
  "reports.col.type": "Type",
  "reports.col.severity": "Severity",
  "reports.col.site": "Site",
  "reports.col.status": "Status",
  "reports.col.ackedAt": "Acknowledged at",
  "reports.col.ackNote": "Acknowledgement note",
  "reports.col.zone": "Zone",
  "reports.col.repeats": "Repeats",
  "reports.col.lat": "Latitude",
  "reports.col.lon": "Longitude",
  "reports.col.speed": "Speed (km/h)",
  "reports.col.ignitionOn": "Ignition",
  "reports.col.source": "Position source",
  "reports.col.segment": "Segment",
  "reports.col.start": "Start",
  "reports.col.end": "End",
  "reports.col.duration": "Duration",
  "reports.col.maxSpeed": "Top speed (km/h)",
  "reports.col.shift": "Shift",
  "reports.col.shiftsUsed": "Shifts used",
  "reports.col.shiftsChecked": "Completed",
  "reports.col.missed": "Missed",
  "reports.col.compliance": "Compliance %",
  "reports.col.submissions": "Checklists submitted",
  "reports.col.faultsOpened": "Faults opened",
  "reports.col.faultsResolved": "Faults resolved",
  "reports.col.mttr": "Mean time to resolve (h)",
  "reports.export.label": "Export",
  "reports.export.csv": "CSV",
  "reports.export.csvSemicolon": "CSV (Turkish Excel)",
  "reports.export.csvSemicolonHint": "Semicolon-separated CSV with decimal commas, which Turkish Excel opens directly.",
  "reports.export.xlsx": "Excel (XLSX)",
  "reports.export.pdf": "PDF",
  "reports.export.working": "Preparing…",
  "reports.export.done": "File ready ({rows} rows).",
  "reports.export.audit": "Every export is logged: who, which report, which filters, how many rows (kept for 1 year).",
  "reports.export.tooMany": "An export can contain at most {max} rows. Narrow the date range or the filters.",
  "reports.export.filters": "Filters",
  "reports.export.generatedAt": "Generated at",
  "reports.export.generatedBy": "Generated by",
  "reports.export.timeZone": "Time zone",
  "reports.export.page": "Page {page} of {pages}",
  "reports.export.info": "Info",
  "reports.export.pdfRowCap": "The PDF shows the first {shown} of {total} rows. Download Excel or CSV for all of them.",
  "reports.export.noRows": "Nothing matches these filters.",
  "reports.export.mapUnavailable": "The map image could not be captured; the route table follows.",
  "reports.usage.title": "Usage report",
  "reports.usage.slug": "usage",
  "reports.usage.subtitle": "Per vehicle and day: ignition, moving and idle time, distance, lifts, utilisation and operators.",
  "reports.usage.totalIgnition": "Total ignition on",
  "reports.usage.totalMoving": "Total moving",
  "reports.usage.totalDistance": "Total distance",
  "reports.usage.avgUtilisation": "Average utilisation",
  "reports.usage.vehiclesUsed": "Vehicles used",
  "reports.usage.noLiftModule": "This vehicle has no lift measurement module",
  "reports.usage.rules": "A record's state lasts until the next record, at most 2 minutes; longer gaps count as \"no data\". Moving: ignition on and at least 1 km/h. Distance uses accurate GNSS fixes only; jumps above 40 km/h and stationary jitter are ignored. Utilisation is ignition time within shifts divided by shift time where the site has shifts, otherwise ignition time divided by the elapsed day. Usage is shown under the vehicle's current site.",
  "reports.events.title": "Event report",
  "reports.events.slug": "events",
  "reports.events.subtitle": "Alarms and events: type, time, vehicle, operator, site, severity and acknowledgement.",
  "reports.events.rules": "Events are filtered by the site the vehicle was on when they happened. Overspeed reporting comes in Phase 2.",
  "reports.route.title": "Route report",
  "reports.route.slug": "route",
  "reports.route.subtitle": "One vehicle, up to 24 hours: the route on a map, stops, zone events and segments.",
  "reports.route.windowHint": "Times are in the time zone of the vehicle's site. An end time before the start runs into the next day.",
  "reports.route.pickVehicle": "Choose the vehicle whose route you want to see.",
  "reports.route.noPoints": "No positions in this window. Indoors there may be no GNSS fix.",
  "reports.route.map": "Map",
  "reports.route.mapLabel": "Route of {vehicle}",
  "reports.route.mapError": "The map could not be loaded. The segments table is below.",
  "reports.route.legendTrack": "Route",
  "reports.route.legendStop": "Stop (over 2 minutes)",
  "reports.route.legendEvent": "Zone event",
  "reports.route.start": "Start",
  "reports.route.end": "End",
  "reports.route.drive": "Driving",
  "reports.route.stop": "Stopped",
  "reports.route.segments": "Segments",
  "reports.route.points": "Positions",
  "reports.route.drivenDistance": "Distance driven",
  "reports.route.drivingTime": "Driving time",
  "reports.route.stopsCount": "Stops",
  "reports.route.geofenceEvents": "Zone events on the route",
  "reports.route.dropped": "{count} implausible position jumps (above 40 km/h) were removed from the route.",
  "reports.route.droppedLabel": "Position jumps removed",
  "reports.route.rules": "Stop: at least 2 minutes below 1 km/h or with the ignition off. Excel and CSV contain every position; the PDF contains the map image and the segments.",
  "reports.checklist.title": "Checklist compliance report",
  "reports.checklist.slug": "checklist-compliance",
  "reports.checklist.subtitle": "Per vehicle and shift: shifts used, checklists completed and missed, faults opened and resolved.",
  "reports.checklist.noShifts": "No shifts defined",
  "reports.checklist.totalCompliance": "Overall compliance",
  "reports.heatmap.title": "Heatmap",
  "reports.heatmap.slug": "heatmap",
  "reports.heatmap.subtitle": "Where safety events cluster on site and where vehicles move the most.",
  "reports.heatmap.mode": "Show",
  "reports.heatmap.mode.events": "Events",
  "reports.heatmap.mode.usage": "Usage",
  "reports.heatmap.events": "Events",
  "reports.heatmap.weight": "Weighted total",
  "reports.heatmap.points": "Moving position samples",
  "reports.heatmap.vehicles": "Vehicles",
  "reports.heatmap.cells": "Cells (~{size} m)",
  "reports.heatmap.unlocated": "Events without a location",
  "reports.heatmap.map": "Map",
  "reports.heatmap.mapLabel": "Heatmap: {mode}",
  "reports.heatmap.mapError": "The map could not be loaded. The summary is above; you can download the cell list as CSV or Excel.",
  "reports.heatmap.mapUnavailable": "The map image could not be captured; the summary and the densest cells follow.",
  "reports.heatmap.legend": "Density",
  "reports.heatmap.legendLow": "Low",
  "reports.heatmap.legendHigh": "High",
  "reports.heatmap.legendAria": "Colour scale: blue to red, low to high density",
  "reports.heatmap.zoomHint": "Zoom in to see individual cells.",
  "reports.heatmap.noEvents": "No events with a known location in this period.",
  "reports.heatmap.noUsage": "No movement recorded in this period. The map needs GNSS positions from the vehicles.",
  "reports.heatmap.truncated": "Showing the {max} densest cells (of {total}). Narrow the dates or the vehicle selection for more detail.",
  "reports.heatmap.rangeTooLong": "The heatmap covers at most 31 days.",
  "reports.heatmap.tooLarge": "The usage heatmap covers at most {max} vehicle-days (e.g. 31 days × 12 vehicles or 7 days × 57 vehicles). Narrow the dates or the vehicle selection.",
  "reports.heatmap.rules.events": "Events are filtered by the site the vehicle was on when they happened. Impacts are weighted by level (1–3), other events count once. Cells are about {size} m across. Checklist alarms have no location and are left out. Other events without a location (e.g. start without a card) are not on the map and are counted separately.",
  "reports.heatmap.rules.usage": "Counts positions with the ignition on and a speed of at least 1 km/h, a verified device clock and a GNSS fix (accuracy at most 15 m when reported), including the per-second track samples. Vehicles are filtered by their current site. Cells are about {size} m across.",
  "reports.heatmap.kvkk": "The map holds no operator or card details, only aggregated position density.",
  "reports.heatmap.sheetCells": "Cells",
  "reports.heatmap.sheetTop": "{count} densest cells",
  "reports.heatmap.col.rank": "Rank",
  "reports.heatmap.col.weight": "Weight",
  "reports.heatmap.col.events": "Events",
  "reports.heatmap.col.points": "Samples",
  "reports.checklist.rules": "Compliance: the share of shifts in which the vehicle was used that have a checklist (from 30 minutes before the shift start). Submissions and faults are filtered by the site at submission; shift use is calculated for the vehicle's current site.",

  "sites.title": "Sites",
  "sites.subtitle": "{count} sites and units",
  "sites.empty": "No sites have been set up for this tenant yet.",

  "site.name": "Name",
  "site.kind": "Type",
  "site.address": "Address",
  "site.vehicles": "Vehicles",
  "site.childCount": "Units",
  "site.noAddress": "No address entered",
  "site.childSites": "Units",
  "site.noChildren": "This site has no units.",
  "site.vehiclesTitle": "Vehicles",
  "site.vehiclesSummary": "{direct} here · {total} including units",
  "site.noVehicles": "No vehicles are registered here or in any unit below.",
  "site.notFound": "Site not found, or you don't have access to it.",

  "siteKind.site": "Site",
  "siteKind.warehouse": "Warehouse",
  "siteKind.department": "Department",
  "siteKind.zone": "Zone",

  "vehicle.fleetNo": "Fleet no",
  "vehicle.type": "Type",
  "vehicle.power": "Power",
  "vehicle.makeModel": "Make / model",
  "vehicle.make": "Make",
  "vehicle.model": "Model",
  "vehicle.serialNo": "Serial no",
  "vehicle.battery": "Battery (V)",
  "vehicle.batteryValue": "{value} V",
  "vehicle.device": "Device",
  "vehicle.noDevice": "Not linked",
  "vehicle.status": "Status",
  "vehicle.site": "Site",
  "vehicle.active": "Active",
  "vehicle.notFound": "Vehicle not found, or you don't have access to it.",

  "vehicleType.forklift": "Forklift",
  "vehicleType.reach_truck": "Reach truck",
  "vehicleType.order_picker": "Order picker",
  "vehicleType.pallet_truck": "Powered pallet truck",
  "vehicleType.other": "Other",

  "power.electric": "Electric",
  "power.diesel": "Diesel",
  "power.lpg": "LPG",

  "status.ready": "Device linked",
  "status.no_device": "Awaiting device",
  "status.inactive": "Inactive",

  "vehicles.title": "Vehicles",
  "vehicles.count": "{shown} of {total} vehicles",
  "vehicles.filterSite": "Site",
  "vehicles.filterType": "Type",
  "vehicles.filterSearch": "Search",
  "vehicles.all": "All",
  "vehicles.clear": "Clear filters",
  "vehicles.empty": "No vehicles match these filters.",
  "vehicles.filtersLabel": "Vehicle filters",

  "vehicleDetail.tabsLabel": "Vehicle sections",
  "vehicleDetail.tab.general": "General",
  "vehicleDetail.tab.live": "Live status",
  "vehicleDetail.tab.events": "Events",
  "vehicleDetail.tab.operators": "Authorised operators",
  "vehicleDetail.tab.device": "Device",
  "vehicleDetail.tab.audit": "Audit",
  "vehicleDetail.identity": "Vehicle identity",
  "vehicleDetail.assignment": "Location and device",
  "vehicleDetail.tabSoon": "The “{tab}” section is coming in a later sprint. There's nothing to show yet.",
  // Sprint 7a: battery and lift modules (ICD v0.2 section 9, ADR 0017)
  "modules.alarmNoBlock": "The vehicle, driving and lifting are never blocked; battery and lift alarms are notifications only.",
  "modules.battery.chargedKwh": "{value} kWh",
  "modules.value.kgPerBar": "{value} kg/bar",
  "modules.lift.noLoad": "no load",
  "modules.lift.estimated": "estimated",
  "modules.lift.estimatedHint": "The device sent no load; the cloud estimated it from the vehicle settings.",
  "modules.batType.lead_acid": "Lead-acid",
  "modules.batType.lithium": "Lithium",
  "modules.settings.title": "Module settings",
  "modules.settings.description": "Battery type, capacity, calibration factor and lift threshold are sent to the device, which computes the state of charge and the load with them. Changes are sent within a minute.",
  "modules.settings.batType": "Battery type",
  "modules.settings.batAh": "Battery capacity",
  "modules.settings.batAhLabel": "Battery capacity (Ah)",
  "modules.settings.kgPerBar": "Calibration factor",
  "modules.settings.kgPerBarLabel": "Calibration factor (kg/bar)",
  "modules.settings.kgPerBarHint": "From a calibration with a known load; the device estimates the load with it.",
  "modules.settings.threshold": "Lift threshold",
  "modules.settings.thresholdLabel": "Lift threshold (bar)",
  "modules.settings.thresholdHint": "A lift cycle starts when the pressure exceeds it.",
  "modules.settings.emptyBarLabel": "Empty-fork pressure (bar)",
  "modules.settings.emptyBarHint": "Not sent to the device; used only for the cloud's estimate when the device sends no load.",
  "modules.settings.ratedLabel": "Rated capacity (kg)",
  "modules.settings.range": "Enter a number between {min} and {max}.",
  "modules.settings.delivery": "Sent to device",
  "modules.settings.note": "Settings only affect measuring; the vehicle, driving and lifting are never blocked.",
  "modules.delivery.none": "Nothing set",
  "modules.delivery.pending": "Waiting for the device",
  "modules.delivery.acked": "Confirmed by the device",
  "modules.delivery.failed": "Not delivered",
  "modules.delivery.kept": "The device keeps its last values",
  "modules.settings.locked": "The device keeps the last value; the field cannot be emptied, enter a new value.",
  "error.module_setting_locked": "A setting already sent to the device cannot be emptied; the device keeps the last value. Enter a new value.",
  "modules.loadError": "Battery and lift data could not be loaded.",
  "modules.value.percent": "{value}%",
  "modules.value.volts": "{value} V",
  "modules.value.amps": "{value} A",
  "modules.value.celsius": "{value} °C",
  "modules.value.kg": "{value} kg",
  "modules.value.bar": "{value} bar",
  "modules.value.meters": "{value} m",
  "modules.battery.title": "Battery",
  "modules.battery.soc": "State of charge",
  "modules.battery.voltage": "Voltage",
  "modules.battery.current": "Current",
  "modules.battery.currentHint": "+ charge, − discharge",
  "modules.battery.temperature": "Temperature",
  "modules.battery.state": "Status",
  "modules.battery.lastCharge": "Last charge",
  "modules.battery.socRange": "{from}% → {to}%",
  "modules.battery.chargedAh": "{value} Ah",
  "modules.battery.noCharge": "No charge in the last 7 days",
  "modules.battery.chargingSince": "Charging started {when}",
  "modules.battery.incomplete": "incomplete record",
  "modules.battery.readingAt": "Last reading: {when}",
  "modules.battery.noReading": "No battery reading yet.",
  "modules.battery.low": "Low charge: put the vehicle on charge.",
  "modules.battery.sparkline": "State of charge, last 24 h",
  "modules.battery.sparklineRange": "Low {min}% · high {max}%",
  "modules.battery.sparklineEmpty": "No battery data in the last 24 h.",
  "modules.battery.nominal": "{value} V battery",
  "modules.state.charging": "Charging",
  "modules.state.discharging": "In use",
  "modules.state.resting": "Resting",
  "modules.state.unknown": "Unknown",
  "modules.lift.title": "Lift",
  "modules.lift.today": "Today",
  "modules.lift.todayValue": "{lifts} lifts · {overloads} overloads",
  "modules.lift.last": "Recent lifts",
  "modules.lift.colTime": "Time",
  "modules.lift.colLoad": "Load",
  "modules.lift.colHeight": "Height",
  "modules.lift.colPressure": "Pressure",
  "modules.lift.overloadBadge": "Overload",
  "modules.lift.none": "No lifts recorded yet.",
  "modules.lift.capacity": "Rated capacity",
  "modules.lift.estimateNote": "The load is the device's estimate with its calibration factor (about ±5%). An overload is only reported; the vehicle and lifting are never blocked.",
  "map.detail.soc": "Battery charge",
  "map.socBadge": "Battery {value}%",

  "users.title": "Users and roles",
  "users.subtitle": "{count} users can access this tenant",
  "users.name": "Name",
  "users.email": "Email",
  "users.role": "Role",
  "users.scope": "Access scope",
  "users.allSites": "All sites",
  "users.siteCount": "{count} sites",
  "users.empty": "This tenant has no users.",
  "users.rolesTitle": "Roles",
  "users.roleHelp.viewer": "Can view screens but not change anything.",
  "users.roleHelp.safety": "Monitors impacts and events, and sees safety reports.",
  "users.roleHelp.fleet_manager": "Manages vehicles, devices, operators and permissions.",
  "users.roleHelp.admin": "Manages users, roles and tenant settings.",
  "users.alarmMail.title": "Alarm e-mails",
  "users.alarmMail.copyAdmins": "Send admins a copy of every alarm",
  "users.alarmMail.hint": "Alarm e-mails normally go to safety officers and fleet managers; admins only get them when nobody in those roles can receive e-mail. When this is on, admins also get a copy of every alarm.",
  "users.alarmMail.readOnly": "Only admins can change this setting.",

  "access.title": "No access",
  "access.body": "You don't have permission to view this screen. Ask your tenant admin if you need it.",

  "soon.title": "Coming soon",
  "soon.body": "The {feature} screen is coming in a later sprint. There's nothing to show yet.",
  "soon.unknownTitle": "Page not found",
  "soon.unknownBody": "There's no screen at this address. Pick something from the menu to carry on.",
  "common.cancel": "Cancel",
  "common.close": "Close",
  "common.save": "Save",
  "common.edit": "Edit",
  "common.delete": "Delete",
  "common.ok": "OK",
  "common.working": "Working…",
  "common.actions": "Actions",
  "common.copy": "Copy",
  "common.copied": "Copied",
  "common.copyFailed": "Copy failed",

  "form.optional": "optional",
  "form.required": "This field is required.",
  "form.tooLong": "Use at most {max} characters.",
  "form.select": "Select…",
  "form.chooseOne": "Please make a choice.",

  "nav.inventory": "Device inventory",

  "error.invalid_credentials": "Wrong email or password.",
  "error.captcha_failed": "The security check failed. Please try again.",
  "error.invalid_code": "The code is wrong or has expired.",
  "error.not_configured": "The server connection has not been configured yet.",
  "error.unknown": "That didn't work. Check the details and try again.",
  "error.forbidden": "You don't have permission to do this.",
  "error.invalid_device_id": "Invalid device ID. Use capital letters, digits and hyphens (e.g. FTS-000123).",
  "error.device_exists": "This device ID is already registered.",
  "error.device_not_found": "Device not found.",
  "error.device_revoked": "This device is blocked and can't be paired.",
  "error.device_busy": "This device is already on another vehicle. Unpair it there first.",
  "error.vehicle_busy": "This vehicle already has a device. Unpair the current one first.",
  "error.vehicle_inactive": "Devices can't be paired with inactive vehicles. Activate the vehicle first.",
  "error.tenant_mismatch": "This device doesn't belong to your company.",
  "error.claim_invalid": "The device ID or label code is wrong, or the device has already been added.",
  "error.invalid_email": "Enter a valid email address.",
  "error.invalid_scope": "Invalid access scope. Admins always see every site; a site selection only applies to other roles.",
  "error.already_member": "This person is already a user of this company.",
  "error.invitation_invalid": "This invitation is no longer valid.",
  "error.wrong_email": "This invitation was sent to a different email address. Sign in with the address it was sent to.",
  "error.scope_changed": "One of the sites in this invitation has been deleted. Ask your admin for a new invitation.",
  "error.site_is_last_scope": "This site is the only one a user has access to. Change that user's access scope before deleting it.",
  "error.vehicle_has_history": "This vehicle has recorded data and can't be deleted. Set it to inactive to take it out of use.",
  "error.rate_limited": "That was too soon. Wait a minute and try again. An invitation can be emailed at most 5 times; if you've reached the limit, create a new invitation.",
  "error.unauthorized": "Your session has expired. Please sign in again.",
  "error.bad_request": "The server didn't accept the request. Reload the page and try again.",
  "error.not_found": "The server couldn't find what was requested.",
  "error.broker_error": "The MQTT server couldn't be reached. Please try again shortly.",
  "error.mail_error": "The email couldn't be sent. Try again shortly, or copy the link and send it yourself.",

  "login.sendLink": "Send sign-in link",

  "deviceState.online": "Online",
  "deviceState.offline": "Offline",
  "deviceState.revoked": "Blocked",

  "device.id": "Device ID",
  "device.vehicle": "Vehicle",
  "device.status": "Status",
  "device.lastSeen": "Last seen",
  "device.neverSeen": "Not connected yet",
  "device.firmware": "Firmware",
  "device.claimedAt": "Added",
  "device.imei": "IMEI",
  "device.iccid": "ICCID",
  "device.unpaired": "Not on a vehicle",

  "devices.title": "Devices",
  "devices.subtitle": "{count} devices · {paired} on vehicles",
  "devices.add": "Add device",
  "devices.pair": "Pair with vehicle",
  "devices.unpair": "Unpair",
  "devices.claimed": "{device} has been added to your company.",
  "devices.empty": "No devices yet.",
  "devices.emptyManager": "Add a telematics unit with the code on its label via “Add device”, then pair it with a vehicle.",
  "devices.emptyViewer": "None of the vehicles you can see has a device yet.",
  "devices.readOnly": "Adding devices and pairing them with vehicles requires the fleet manager role.",

  "claim.title": "Add device",
  "claim.description": "Add a telematics unit you received to your company. The device ID and label code are printed on the label on the device.",
  "claim.code": "Label code",
  "claim.codeHint": "The 12-character code (e.g. 0A1B-2C3D-4E5F). Hyphens and spaces don't matter. Each code works only once.",
  "claim.codeFormat": "The code has 12 characters (0–9, A–F).",
  "claim.submit": "Add device",
  "claim.demoHint": "Sample label codes for the demo: {codes}",

  "pair.titleDevice": "Pair {device} with a vehicle",
  "pair.titleVehicle": "Pair a device with {vehicle}",
  "pair.description": "A vehicle carries one device at a time. The pairing history is kept.",
  "pair.vehicle": "Vehicle",
  "pair.vehicleHint": "Only active vehicles without a device are listed.",
  "pair.device": "Device",
  "pair.noVehicles": "No vehicle available: every active vehicle already has a device.",
  "pair.noDevices": "No device available. Add one on the Devices screen first.",
  "pair.submit": "Pair",

  "unpair.title": "Unpair device",
  "unpair.body": "{device} will be removed from {vehicle}. The device stays with your company and can be paired with another vehicle.",
  "unpair.submit": "Unpair",

  "vehicleDevice.title": "Telematics device",
  "vehicleDevice.none": "No device on this vehicle.",
  "vehicleDevice.inactive": "The vehicle is inactive, so no device can be paired.",
  "vehicleDevice.pair": "Pair device",

  "inventory.title": "Device inventory",
  "inventory.subtitle": "{total} registered devices · {inventory} in inventory",
  "inventory.register": "Register devices",
  "inventory.tenant": "Company",
  "inventory.inStock": "In inventory",
  "inventory.onVehicle": "on a vehicle",
  "inventory.rotate": "Rotate secret",
  "inventory.revoke": "Block",
  "inventory.unrevoke": "Unblock",
  "inventory.release": "Return to inventory",
  "inventory.filter": "Status",
  "inventory.filterInventory": "In inventory",
  "inventory.filterClaimed": "With a company",
  "inventory.filterRevoked": "Blocked",
  "inventory.filtersLabel": "Inventory filters",
  "inventory.searchPlaceholder": "Device ID, IMEI, ICCID",
  "inventory.empty": "No devices registered yet.",
  "inventory.noMatch": "No devices match the filters.",
  "inventory.note": "Only Expartise platform admins see this screen. Secrets and label codes are stored as hashes only and cannot be shown again later.",

  "register.title": "Register devices",
  "register.description": "One device per line. Registration generates a secret and a label code for each device.",
  "register.lines": "Devices",
  "register.linesHint": "Format: DEVICE_ID[,IMEI[,ICCID]], e.g. FTS-000123,350000000000123",
  "register.lineErrors": "Lines with errors",
  "register.lineNo": "Line {line}:",
  "register.lineInvalid": "invalid device ID (capital letters, digits and hyphens; 3–32 characters)",
  "register.lineDuplicate": "this device ID is already in the list or registered",
  "register.lineTooMany": "at most three fields are allowed",
  "register.empty": "Enter at least one device.",
  "register.count": "{count} devices to register",
  "register.submit": "Register",
  "register.resultTitle": "{count} devices registered",
  "register.secretWarning": "Secrets and label codes are shown only now. Download the CSV or copy them before closing this window; they cannot be shown again.",
  "register.partialFailure": "{device} could not be registered: {reason} That line and the ones after it were not processed.",
  "register.downloadCsv": "Download CSV",
  "register.labelHint": "The secret is flashed into the device. The label code is printed on the device label; the customer uses it to add the device.",
  "register.brokerNotSynced": "Registered, but the MQTT server could not be updated: {devices}. These devices can't connect with the secret shown; rotate their secrets from the inventory before installing them.",

  "secret.secret": "Secret",
  "secret.claimCode": "Label code",
  "secret.copySecret": "Copy the secret of {device}",
  "secret.copyCode": "Copy the label code of {device}",
  "secret.savedClose": "I've saved it, close",

  "rotate.title": "Rotate the secret of {device}?",
  "rotate.body": "A new secret will be generated. The old one stops working immediately, and the device can't connect until the new secret is flashed.",
  "rotate.submit": "Rotate secret",
  "rotate.resultTitle": "New secret for {device}",
  "rotate.warning": "This secret is shown only now. Copy it and flash it into the device; it cannot be shown again.",
  "rotate.brokerNotSynced": "The new secret is saved, but the MQTT server could not be updated, so the device can't connect with it yet. Rotate the secret again shortly.",

  "revoke.title": "Block {device}?",
  "revoke.body": "A blocked device can't connect and its data is not stored. Its vehicle pairing is kept, and you can unblock it later.",
  "revoke.unTitle": "Unblock {device}?",
  "revoke.unBody": "The device will be able to connect and send data again. Depending on the server setup, a new secret may be issued; if so, it is shown only once.",
  "revoke.syncTitle": "MQTT server not updated",
  "release.brokerNotSynced": "{device} is back in inventory and its secret is cancelled, but the MQTT server could not be updated, so the device may keep its current connection. Block the device to retry the update.",
  "revoke.brokerNotSynced": "{device} is blocked and its data is no longer stored. However, the MQTT server could not be updated, so the device may stay connected.",
  "revoke.unBrokerNotSynced": "{device} is unblocked, but the change could not be passed to the MQTT server, so the device may not be able to connect yet. Rotate its secret so it can connect.",
  "revoke.unSecretWarning": "The device is unblocked. Its old secret no longer works; flash this new secret into the device. It is shown only now and cannot be shown again.",

  "release.title": "Return {device} to inventory?",
  "release.body": "The device is taken back from {tenant} and unpaired from its vehicle. A new label code is generated and the old one stops working. The device's secret is also cancelled: rotate it and load the new one into the device before handing it to another customer.",
  "release.submit": "Return to inventory",
  "release.resultTitle": "{device} is back in inventory",
  "release.warning": "The new label code is shown only now. Relabel the device with this code.",

  "sites.add": "Add site",
  "site.addChild": "Add sub-unit",
  "site.parent": "Parent",
  "site.noParent": "None (top-level site)",
  "site.parentRequired": "Choose a parent.",
  "site.parentHintManager": "Creating a top-level site requires the tenant admin role.",
  "site.rootLocked": "Only tenant admins can move top-level sites.",
  "site.lat": "Latitude",
  "site.lon": "Longitude",
  "site.latHint": "Between −90 and 90, e.g. 40.7654",
  "site.lonHint": "Between −180 and 180, e.g. 29.9432",
  "site.latInvalid": "Latitude must be a number between −90 and 90.",
  "site.lonInvalid": "Longitude must be a number between −180 and 180.",
  "site.coordPair": "Enter latitude and longitude together.",
  "siteForm.createTitle": "New site or unit",
  "siteForm.editTitle": "Edit site",
  "siteDelete.title": "Delete “{name}”?",
  "siteDelete.body": "This cannot be undone.",
  "siteDelete.hasChildren": "This site has sub-units. Delete them or move them to another site first.",
  "siteDelete.hasVehicles": "Vehicles are registered at this site. Move them to another site first.",

  "vehicles.add": "Add vehicle",
  "vehicleForm.createTitle": "New vehicle",
  "vehicleForm.editTitle": "Edit {fleetNo}",
  "vehicleForm.fleetNoTaken": "This fleet number is already in use.",
  "vehicleForm.noBattery": "Not specified",
  "vehicleForm.batteryHint": "Battery voltage of electric vehicles.",
  "vehicleForm.activeHint": "Set vehicles that are out of service to inactive. Inactive vehicles can't be paired with a device.",
  "vehicleForm.unauthorizedStart": "Start-without-card alarm",
  "vehicleForm.unauthorizedStartHint": "Turn this off for a vehicle that has no card reader fitted.",
  "vehicleForm.unauthorizedStartNoReader": "The device on this vehicle has no card reader, so this alarm cannot occur.",

  "tenantForm.open": "New company",
  "tenantForm.title": "New company",
  "tenantForm.description": "Open a new customer company (tenant). You can add its sites and users afterwards.",
  "tenantForm.name": "Company name",
  "tenantForm.nameTooShort": "The company name needs at least 2 characters.",
  "tenantForm.slug": "Short name",
  "tenantForm.slugHint": "A unique short identifier: lower-case letters, digits and hyphens.",
  "tenantForm.slugInvalid": "Start with a lower-case letter or digit and use only lower-case letters, digits and hyphens (2–41 characters).",
  "tenantForm.slugTaken": "Another company already uses this short name.",
  "tenantForm.submit": "Create company",

  "members.you": "(you)",
  "members.changeRole": "Change role",
  "members.remove": "Remove user",
  "members.roleTitle": "Role of {name}",
  "members.adminNeedsAllSites": "remove the site restriction first",
  "members.removeTitle": "Remove {name}?",
  "members.removeBody": "The user loses access to {tenant}. Their account is not deleted, and you can invite them again later.",
  "members.adminSeatsNote": "To appoint an admin, change an admin's role or remove an admin, please contact Expartise.",

  "invite.open": "Invite user",
  "invite.title": "Invite user",
  "invite.description": "Create an invitation link for {tenant}.",
  "invite.selectedSites": "Selected sites",
  "invite.chooseSites": "Choose at least one site.",
  "invite.adminAllSites": "Admins always have access to every site.",
  "invite.scopeHint": "Access to a site includes all of its sub-units.",
  "invite.submit": "Create invitation",
  "invite.createdTitle": "Invitation created",
  "invite.createdBody": "{email} has been invited as {role}. The link is valid until {date}.",
  "invite.link": "Invitation link",
  "invite.copyLink": "Copy invitation link",
  "invite.sendEmail": "Send by email",
  "invite.sendAgain": "Send again",
  "invite.sending": "Sending…",
  "invite.emailSent": "Invitation email sent to {email}.",
  "invite.emailSentDemo": "(No real email is sent in demo mode.)",
  "invite.sendHint": "You can email the invitation from here, or copy the link and pass it on yourself. The link is shown only now; if you lose it, create a new invitation.",

  "invitations.title": "Invitations",
  "invitations.empty": "No invitations for this company yet.",
  "invitations.status": "Status",
  "invitations.expires": "Expires",
  "invitations.revoke": "Revoke",
  "invitations.revokeTitle": "Revoke the invitation for {email}?",
  "invitations.revokeBody": "The link stops working immediately. You can create a new invitation if needed.",
  "invitationStatus.pending": "Pending",
  "invitationStatus.accepted": "Accepted",
  "invitationStatus.revoked": "Revoked",
  "invitationStatus.expired": "Expired",

  "acceptInvite.title": "Invitation",
  "acceptInvite.summary": "{tenant} has invited you to the Forklift Tracking System console as {role}.",
  "acceptInvite.expires": "The invitation is valid until {date}.",
  "acceptInvite.loadErrorBody": "We couldn't load the invitation. Check your connection and reload the page.",
  "acceptInvite.unknownTitle": "Invitation not found",
  "acceptInvite.unknownBody": "The link may be wrong or incomplete. Ask the person who invited you to send it again.",
  "acceptInvite.acceptedTitle": "This invitation has already been accepted",
  "acceptInvite.acceptedBody": "{tenant} is linked to your account. Sign in to the console to continue.",
  "acceptInvite.revokedTitle": "This invitation has been revoked",
  "acceptInvite.revokedBody": "If you still need access, ask your {tenant} admin for a new invitation.",
  "acceptInvite.expiredTitle": "This invitation has expired",
  "acceptInvite.expiredBody": "Invitations are valid for 7 days. Ask your {tenant} admin for a new one.",
  "acceptInvite.signInLinkIntro": "To accept, enter the email address the invitation was sent to and we'll email you a sign-in link.",
  "acceptInvite.signInPasswordIntro": "To accept, sign in with the account the invitation was sent to.",
  "acceptInvite.usePassword": "Have a password? Sign in with it",
  "acceptInvite.useLink": "Email me a sign-in link instead",
  "acceptInvite.checkInbox": "Check your inbox",
  "acceptInvite.checkInboxBody": "We sent a sign-in link to {email}. Click the link in that email in this browser and you'll come back here to accept the invitation.",
  "acceptInvite.useOtherEmail": "Use a different address",
  "acceptInvite.demoNote": "No emails are sent in demo mode. Sign in with one of the sample accounts from the login screen (password: demo).",
  "acceptInvite.signedInAs": "Signed in as {email}.",
  "acceptInvite.displayName": "Your name",
  "acceptInvite.displayNameHint": "Other users will see you under this name.",
  "acceptInvite.accept": "Accept invitation",
  "acceptInvite.wrongEmail": "This invitation was sent to a different email address. You are signed in as {email}; please sign in with the address the invitation was sent to.",
  "acceptInvite.switchAccount": "Sign in with another account",
  "acceptInvite.alreadyMember": "You already have access to this company.",
  "acceptInvite.toConsole": "Go to the console",

  "map.title": "Live map",
  "map.subtitle": "{tenant} · vehicles right now",
  "map.listLabel": "Vehicles",
  "map.search": "Search fleet no or model",
  "map.filterLabel": "Filter by state",
  "map.filter.all": "All",
  "map.state.working": "Working",
  "map.state.idle": "Idling",
  "map.state.off": "Ignition off",
  "map.state.offline": "Offline",
  "map.state.alarm": "Alarm",
  "map.noMatch": "No vehicle matches this filter.",
  "map.empty": "No tracked vehicles",
  "map.emptyBody": "To see a vehicle on the map, pair a telematics unit with it (Devices screen).",
  "map.noData": "No data yet",
  "map.noPosition": "No position",
  "map.stale": "Last known position",
  "map.feed.connecting": "Connecting…",
  "map.feed.live": "Live",
  "map.feed.reconnecting": "Reconnecting…",
  "map.loadError": "Live data could not be loaded. It will retry within a minute.",
  "map.mapError": "The base map could not be loaded; the vehicle list keeps working.",
  "map.fitAll": "Show all",
  "map.sitePin": "Site: {name}",
  "map.vehiclePin": "{fleetNo}, {state}",
  "map.mapLabel": "Vehicle map",
  "map.detail.close": "Close",
  "map.detail.openVehicle": "Open vehicle record",
  "map.detail.ignition": "Ignition",
  "map.detail.seat": "Seat",
  "map.detail.belt": "Seat belt",
  "map.detail.speed": "Speed",
  "map.detail.operator": "Operator card",
  "map.detail.lastData": "Last data",
  "map.detail.positionAt": "Position time",
  "map.detail.lastEvent": "Last event",
  "map.detail.link": "Connection",
  "map.detail.supply": "Supply voltage",
  "map.detail.device": "Device",
  "map.value.on": "On",
  "map.value.off": "Off",
  "map.value.occupied": "Occupied",
  "map.value.empty": "Empty",
  "map.value.fastened": "Fastened",
  "map.value.unfastened": "Not fastened",
  "map.value.noCard": "No card",
  "map.value.kmh": "{value} km/h",
  "map.value.volts": "{value} V",
  "map.value.accuracy": "±{value} m",
  "map.event.impact": "Impact",
  "map.event.tamper": "Tamper",
  "map.event.seat_violation": "Seat violation",
  "map.event.belt_violation": "Belt violation",
  "map.event.power_loss": "Power lost",
  "map.event.power_restore": "Power restored",
  "map.event.card_ok": "Card login",
  "map.event.card_denied": "Card denied",
  "map.event.logout": "Logout",
  // Sprint 7a: module events
  "map.event.lift": "Lift",
  "map.event.overload": "Overload",
  "map.event.charge_start": "Charging started",
  "map.event.charge_end": "Charging ended",
  "map.event.deep_discharge": "Battery deeply discharged",
  "map.event.bat_overtemp": "Battery overheating",
  "map.privacyNote": "The operator card is personal data; only its last four characters are shown.",
  "map.event.other": "Other event",
  "map.value.rssi": "{value} dBm",
  "map.value.link.gsm": "Cellular (2G/3G)",
  "map.value.link.lte": "Cellular (4G/LTE)",
  "map.value.link.wifi": "Wi-Fi",
  "inventory.newClaimCode": "Renew label code",
  "newClaim.title": "Issue a new label code for {device}?",
  "newClaim.body": "Use this if the label was lost. A new label code is issued and the old one stops working. The device secret is revoked as well; rotate it afterwards and load it into the device.",
  "newClaim.submit": "Issue new code",
  "newClaim.resultTitle": "New label code for {device}",

  "error.card_in_use": "This card number is active for another operator. Mark that card as lost or revoked first.",
  "error.invalid_card": "Invalid card number. Enter 8–20 hexadecimal characters (0–9, A–F).",
  "error.invalid_permission": "Choose either a site or a single vehicle for the permission.",
  "error.invalid_left_on": "The last working day cannot be more than a month ago. Check the date; a wrong date would erase the personal data early.",
  "error.operator_left": "This operator's personal data has been erased (KVKK). The record can no longer be changed.",
  "error.invalid_contact": "This person cannot see the site. A night contact must be a user of the company who can see this site.",
  "error.invalid_alert_settings": "Invalid working hours. Choose at least one working day; the start time must be before the end time.",
  "error.already_acknowledged": "This alarm has already been acknowledged.",
  "error.note_too_long": "The note can be at most 500 characters.",
  "map.value.unknownCard": "unknown card",
  "opStatus.active": "Active",
  "opStatus.expiring": "Document expiring",
  "opStatus.suspended": "Suspended",
  "opStatus.inactive": "Inactive",
  "opStatus.left": "Left",
  "operators.title": "Operators",
  "operators.subtitle": "{count} operators",
  "operators.add": "Add operator",
  "operators.filtersLabel": "Operator filters",
  "operators.search": "Search by name or employee no.",
  "operators.filterStatus": "Status",
  "operators.allCurrent": "All except leavers",
  "operators.empty": "No operators yet",
  "operators.emptyManager": "Add an operator, register their card and choose which vehicles they may drive.",
  "operators.emptyViewer": "Operators are added by the safety officer or fleet manager.",
  "operators.noMatch": "No operators match the filter",
  "operators.readOnly": "You can view operators. Cards, documents and permissions are managed by safety officers and above.",
  "operators.back": "Back to operators",
  "operator.fullName": "Full name",
  "operator.employeeNo": "Employee no.",
  "operator.site": "Home site",
  "operator.siteAll": "Whole company",
  "operator.siteHint": "Controls who can see the operator. Which vehicles they may drive comes from permissions.",
  "operator.status": "Status",
  "operator.cards": "Cards",
  "operator.documents": "Documents",
  "operator.permissions": "Permissions",
  "operator.nextExpiry": "Next document expiry",
  "operator.identity": "Details",
  "operator.leftOn": "Last working day",
  "operator.leftOnHint": "The operator may drive until the end of this day. Their personal data is erased automatically six months later.",
  "operator.authorizedVehicles": "Vehicles they may drive today",
  "operator.noVehicles": "No vehicles at the moment",
  "operator.addTitle": "Add operator",
  "operator.editTitle": "Edit operator",
  "operator.kvkkNote": "Name and employee number are personal data (KVKK). A leaver's data is erased automatically after six months.",
  "operator.activeLabel": "Active (an inactive operator's cards work on no vehicle)",
  "operator.leave": "Has left",
  "operator.leaveTitle": "Mark {name} as having left?",
  "operator.leaveDescription": "After the last working day their cards stop working on every vehicle.",
  "operator.leaveSubmit": "Mark as left",
  "operator.suspendedNote": "A required document has expired. The operator cannot start any vehicle until it is renewed.",
  "operator.leftNote": "Last working day: {day}. Personal data will be erased automatically six months after this date.",
  "operator.anonymizedNote": "This operator's personal data was erased under KVKK. The record is kept only for past reports.",
  "operator.notFound": "Operator not found",
  "card.uid": "Card number",
  "card.tech": "Card type",
  "card.status": "Status",
  "card.operator": "Operator",
  "card.createdAt": "Added",
  "card.add": "Add card",
  "card.addTitle": "Add a card for {name}",
  "card.addDescription": "Enter the number as the reader shows it; spaces and colons are removed automatically.",
  "card.uidHint": "8–20 hexadecimal characters, e.g. 04A1B2C3",
  "card.uidFormat": "Enter 8–20 hexadecimal characters (0–9, A–F).",
  "card.uidNormalized": "Will be saved as: {uid}",
  "card.none": "No cards",
  "card.markLost": "Lost",
  "card.revoke": "Revoke",
  "card.lostTitle": "Mark the card as lost?",
  "card.revokeTitle": "Revoke the card?",
  "card.statusBody": "Card {uid} of {name} stops working on every vehicle within seconds. This cannot be undone; if the card turns up, add it again.",
  "cardStatus.active": "Active",
  "cardStatus.lost": "Lost",
  "cardStatus.revoked": "Revoked",
  "cardTech.mifare": "MIFARE (13.56 MHz)",
  "cardTech.hid": "HID",
  "cardTech.em125": "125 kHz (EM)",
  "cards.title": "Cards",
  "cards.subtitle": "{count} active cards",
  "cards.filtersLabel": "Card filters",
  "cards.search": "Search by card number",
  "cards.showInactive": "Include lost and revoked cards",
  "cards.empty": "No cards yet",
  "cards.emptyHint": "Cards are added on the operator's page.",
  "cards.noMatch": "No cards match the search",
  "cards.note": "A card marked lost or revoked reaches the vehicles within seconds. A vehicle that is offline gets the current list as soon as it reconnects.",
  "doc.kind": "Document",
  "doc.label": "Note",
  "doc.validUntil": "Valid until",
  "doc.required": "Required",
  "doc.requiredLabel": "Required document",
  "doc.requiredHint": "When a required document expires the operator is suspended automatically. The console warns 30, 15 and 7 days ahead.",
  "doc.add": "Add document",
  "doc.addTitle": "Add document",
  "doc.editTitle": "Edit document",
  "doc.none": "No documents",
  "doc.expired": "Expired",
  "doc.withinDays": "Expires within {days} days",
  "doc.delete": "Delete document",
  "doc.deleteTitle": "Delete the document?",
  "doc.deleteBody": "The “{kind}” record will be deleted. If it was required, the operator's permission no longer depends on it.",
  "docKind.operator_certificate": "Operator certificate",
  "docKind.driving_licence": "Driving licence",
  "docKind.training": "Training certificate",
  "docKind.other": "Other",
  "perm.operator": "Operator",
  "perm.scope": "Scope",
  "perm.scopeSite": "{site} (all vehicles)",
  "perm.scopeSiteType": "{site} · {type} only",
  "perm.scopeVehicle": "{vehicle} only",
  "perm.scopeSiteOption": "Site (including sub-areas; vehicles added later are covered too)",
  "perm.scopeVehicleOption": "A single vehicle",
  "perm.site": "Site",
  "perm.siteHint": "All sub-areas of the site are included.",
  "perm.vehicleType": "Vehicle type",
  "perm.allTypes": "All types",
  "perm.vehicle": "Vehicle",
  "perm.validUntil": "Valid until",
  "perm.validUntilHint": "Leave empty for no end date. The device itself enforces the date, even while offline.",
  "perm.openEnded": "No end date",
  "perm.expired": "Expired",
  "perm.add": "Grant permission",
  "perm.addTitle": "Grant permission",
  "perm.addTitleFor": "Grant {name} a permission",
  "perm.addDescription": "The operator may start these vehicles while their card is active and required documents are valid.",
  "perm.none": "No permissions yet",
  "perm.remove": "Remove permission",
  "perm.removeTitle": "Remove the permission?",
  "perm.removeBody": "The “{scope}” permission of {name} will be removed. Vehicles are updated within seconds.",
  "permissions.title": "Permissions",
  "permissions.subtitle": "Which operator may drive which vehicles",
  "permissions.showExpired": "Include expired permissions",
  "permissions.empty": "No permissions yet",
  "permissions.emptyHint": "Grant an operator a site, a vehicle type or a single vehicle.",
  "permissions.note": "Changes reach the vehicles within seconds. Offline vehicles get the current list as soon as they reconnect.",
  "vehicleOps.title": "Operators who may drive this vehicle",
  "vehicleOps.until": "Permission ends",
  "vehicleOps.none": "No operator may drive this vehicle today.",
  "vehicleOps.note": "Worked out from permissions, active cards and document dates; the device receives exactly this list.",
  "authlist.column": "Card list",
  "authlist.synced": "Up to date",
  "authlist.pending": "Sending",
  "authlist.error": "Error",
  "authlist.none": "Not sent",
  "authlist.syncedAt": "Device confirmed the list ({when})",
  "authlist.pendingHint": "The current list is waiting for the device to confirm. An offline device gets it when it reconnects.",
  "authlist.errorHint": "The list could not be delivered: {error}. Retried every minute.",
  "authlist.noneHint": "No list has been sent to this device yet.",
  "authlist.noReader": "No card reader",
  "authlist.noReaderHint": "This device has no card reader, so it gets no card list and raises no card alarms (start without card, unauthorised card).",
  "authlist.syncNow": "Send lists now",
  "authlist.syncHint": "Changes are sent every minute anyway; use this if you don't want to wait.",
  "authlist.syncDone": "Lists sent.",
  "authlist.syncFailed": "The lists could not be sent right now; they will be retried within a minute.",

  /* ---- Sprint 6a: alarms (ADR 0011) ---- */
  "alarm.type.impact": "Impact",
  "alarm.type.unauthorized_start": "Start without card",
  "alarm.type.card_denied": "Unauthorised card attempt",
  "alarm.type.seat_violation": "Seat violation",
  "alarm.type.belt_violation": "Seat belt violation",
  "alarm.type.tamper": "Tampering",
  "alarm.type.power_loss": "Power lost",
  "alarm.type.geofence_enter": "Entered a no-go zone",
  "alarm.type.geofence_exit": "Left the work area",
  "alarm.type.geofence_dwell": "Stayed too long in a zone",
  // Sprint 7a: module alarms
  "alarm.type.overload": "Overload",
  "alarm.type.deep_discharge": "Battery deeply discharged",
  "alarm.type.bat_overtemp": "Battery overheating",
  "alarm.type.other": "Other event",
  "alarm.typeWithSeverity": "{type} · Level {level}",
  "alarm.status.open": "Open",
  "alarm.status.acknowledged": "Acknowledged",
  "alarm.badge.offHours": "Off hours",
  "alarm.badge.delayed": "Delayed",
  "alarm.badge.offHoursHint": "The event happened outside the site's working hours.",
  "alarm.badge.delayedHint": "The device sent this event late, once its connection came back.",
  "alarm.badge.timeUnknown": "Time unknown",
  "alarm.badge.timeUnknownHint": "The device clock could not be verified; the time shown is when the event reached the system.",
  "alarm.badge.repeat": "×{count} repeats, last: {time}",
  "alarm.badge.repeatNoTime": "×{count} repeats",
  "alarm.badge.repeatHint": "The same event happened again on this vehicle within 30 minutes; no new alarm or notification was created.",
  "alarm.timeUnknown": "Time of event unknown (the device clock could not be verified)",
  "alarm.noBlock": "The vehicle is not blocked; an impact is only a notification.",

  "alarms.title": "Alarms",
  "alarms.subtitle": "Impacts and safety events. Times are shown in Turkey time.",
  "alarms.filtersLabel": "Alarm filters",
  "alarms.filter.status": "Status",
  "alarms.filter.open": "Open",
  "alarms.filter.acknowledged": "Acknowledged",
  "alarms.filter.all": "All",
  "alarms.filter.type": "Type",
  "alarms.filter.allTypes": "All types",
  "alarms.filter.site": "Site",
  "alarms.filter.allSites": "All sites",
  "alarms.filter.from": "From",
  "alarms.filter.to": "To",
  "alarms.filter.clear": "Clear filters",
  "alarms.filter.dateError": "The end date can't be before the start date.",
  "alarms.shown": "Shown: {count}",
  "alarms.limitNote": "Showing the newest {limit} alarms. Narrow the date range to see older ones.",
  "alarms.col.time": "Time",
  "alarms.col.type": "Type",
  "alarms.col.vehicle": "Vehicle",
  "alarms.col.site": "Site",
  "alarms.col.operator": "Operator",
  "alarms.col.status": "Status",
  "alarms.emptyOpen": "No open alarms.",
  "alarms.empty": "No alarms match these filters.",
  "alarms.loadError": "Could not load alarms.",
  "alarms.rowLink": "Details of the {type} alarm, {time}",

  "alarmDetail.back": "Back to alarms",
  "alarmDetail.heading": "{type} · {vehicle}",
  "alarmDetail.notFound": "Alarm not found",
  "alarmDetail.notFoundHint": "It may have been removed, or you may not have access to its site.",
  "alarmDetail.facts": "Event details",
  "alarmDetail.occurredAt": "Event time",
  "alarmDetail.receivedAt": "Received",
  "alarmDetail.type": "Type",
  "alarmDetail.vehicle": "Vehicle",
  "alarmDetail.site": "Site",
  "alarmDetail.device": "Device",
  "alarmDetail.operator": "Operator",
  "alarmDetail.noOperator": "No card presented",
  "alarmDetail.workTime": "Working hours",
  "alarmDetail.inHours": "Within working hours",
  "alarmDetail.offHours": "Outside working hours",
  "alarmDetail.delayedNote": "Delayed: the event happened at {time} and reached the system at {received}.",
  "alarmDetail.timeUnknownNote": "Time of event unknown (the device clock could not be verified). The alarm was recorded with the time it reached the system and the vehicle's current site.",
  "alarmDetail.repeats": "Repeats",
  "alarmDetail.notifyError": "Automatic notifications could not be sent for this alarm. Please let the people concerned know yourself.",
  "alarmDetail.noRecipients": "Nobody was notified about this alarm: no one at this site can receive notifications. Add a safety officer, fleet manager or company admin to the company.",
  "alarmDetail.status": "Status",
  "alarmDetail.timeZoneNote": "Times are in Turkey time.",

  "alarmNotify.title": "Who was notified",
  "alarmNotify.none": "There are no notification records you can see.",
  "alarmNotify.ownOnly": "Only notifications sent to you are shown. Safety officers and above see every notification for the site.",
  "alarmNotify.recipient": "Recipient",
  "alarmNotify.channel": "Channel",
  "alarmNotify.kind": "Notification",
  "alarmNotify.status": "Status",
  "alarmNotify.sentAt": "Sent",
  "alarmNotify.you": "{name} (you)",
  "alarmNotify.unknownUser": "Team member",
  "alarmNotify.dueAt": "Scheduled: {time}",
  "alarmChannel.email": "E-mail",
  "alarmChannel.sms": "SMS",
  "alarmChannel.push": "Push notification",

  /* Sprint 7b (ADR 0019): PWA and web push */
  "nav.profile": "My profile",
  "profile.title": "My profile",
  "profile.subtitle": "Your account, your password and the devices that receive notifications",
  "profile.account": "Account",
  "profile.name": "Name",
  "profile.email": "E-mail",
  "password.title": "Password",
  "password.intro": "Set a password to sign in to the console with your e-mail and password. You can create one here even if you have only used e-mail links so far.",
  "password.new": "New password",
  "password.repeat": "Repeat new password",
  "password.show": "Show password",
  "password.rules": "{min} to {max} characters, and not the same as your e-mail address. A long sentence or a mix of letters, digits and symbols is safest.",
  "password.strength.weak": "Strength: weak",
  "password.strength.fair": "Strength: fair",
  "password.strength.strong": "Strength: strong",
  "password.submit": "Save password",
  "password.updated": "Your password has been updated.",
  "password.err.required": "Enter a new password.",
  "password.err.tooShort": "The password must be at least {min} characters.",
  "password.err.tooLong": "The password can be at most {max} characters (accented letters count as two).",
  "password.err.sameAsEmail": "The password cannot be your e-mail address.",
  "password.err.repeatRequired": "Enter the password again.",
  "password.err.mismatch": "The two passwords do not match.",
  "push.title": "Notifications",
  "push.intro": "Immediate alarm notifications that reach you by e-mail also arrive on the devices where you turn notifications on. The morning digest and document reminders stay e-mail only. Notifications never show the operator's name, as they appear on lock screens.",
  "push.disabledAdmin": "Push notifications are not set up on the server yet (VAPID keys missing). Run the “VAPID keys” workflow in GitHub Actions (docs/ops/cloud-setup.md, section 4b). E-mail notifications keep working.",
  "push.disabledUser": "Push notifications are not available right now. Alarm notifications still arrive by e-mail.",
  "push.configError": "Notification settings could not be loaded.",
  "push.env.ios_install": "To get notifications on iPhone or iPad, first add FTS to your Home Screen: in Safari tap Share → Add to Home Screen. Then open the app from the Home Screen and come back to this page.",
  "push.env.denied": "Notifications are blocked for FTS in this browser. Allow them in the browser's site settings, then reload the page.",
  "push.env.unsupported": "This browser does not support push notifications. Use a current version of Chrome, Edge, Firefox or Safari.",
  "push.thisDevice": "This device",
  "push.thisDeviceOn": "Notifications are on for this device.",
  "push.thisDeviceOff": "Notifications are off for this device.",
  "push.enable": "Turn on notifications on this device",
  "push.disable": "Turn off on this device",
  "push.enabled": "Notifications are now on for this device.",
  "push.disabled": "Notifications are now off for this device.",
  "push.permissionNote": "Your browser will ask for permission.",
  "push.notRemembered": "This device doesn't remember your session, so notifications can't be turned on here (shared devices). To get them on your own device, sign out and sign in again with \"Remember me\" ticked.",
  "push.stale": "Notifications need renewing: the server's notification key changed and this device receives nothing right now.",
  "push.renew": "Renew notifications",
  "push.devices": "My devices receiving notifications",
  "push.noDevices": "You have not turned on notifications on any device yet.",
  "push.added": "Added: {date}",
  "push.lastOk": "Last delivered: {date}",
  "push.failures": "{n} failed attempt(s)",
  "push.remove": "Remove",
  "push.removeTitle": "Remove this device?",
  "push.removeText": "{name} will no longer receive notifications.",
  "push.test": "Send a test notification",
  "push.testResult": "Test notification sent to {sent} device(s). Not delivered: {failed}. Removed: {removed}.",
  "push.err.denied": "Notification permission was not granted.",
  "push.err.noSw": "The notification service is not ready on this page. Reload and try again.",
  "push.err.subscribeFailed": "This device could not be registered with the push service. Please try again.",
  "error.network": "No connection. Check your internet connection and try again.",
  "error.weak_password": "This password is too weak or easy to guess. Choose a longer one or mix letters, digits and symbols.",
  "error.same_password": "The new password is the same as your current one. Choose a different password.",
  "error.reauthentication_needed": "For security, sign out and sign in again with an e-mail link, then change your password.",
  "error.not_in_demo": "Passwords cannot be changed in demo mode; demo accounts use a fixed sample password.",
  "error.push_disabled": "Push notifications are not set up on the server.",
  "error.invalid_push_subscription": "This device's notification registration is invalid. Please try again.",
  "error.push_device_limit": "Notifications can be on for at most 10 devices. Remove a device you no longer use first.",
  "pwa.offline": "No connection",
  "pwa.updateReady": "A new version is available.",
  "pwa.reload": "Reload",
  "pwa.iosHint": "To install, tap Share → Add to Home Screen in Safari.",
  "lazy.loadError": "This part could not be loaded. Reload the page.",
  "lazy.reload": "Reload page",
  "pwa.dismiss": "Dismiss",
  "cf.offline": "No connection. Check your internet connection and try again.",
  "alarmKind.immediate": "Immediate",
  "alarmKind.digest_item": "Morning digest",
  "alarmKind.document_reminder": "Document reminder",
  "alarmNStatus.pending": "Queued",
  "alarmNStatus.sending": "Sending",
  "alarmNStatus.sent": "Sent",
  "alarmNStatus.failed": "Failed",
  "alarmNStatus.skipped": "Not sent",

  "alarmAck.title": "Acknowledgement",
  "alarmAck.button": "Acknowledge alarm",
  "alarmAck.noteLabel": "Note",
  "alarmAck.noteHint": "Briefly say what was done (up to 500 characters). An acknowledgement can't be undone.",
  "alarmAck.tooLong": "The note can be at most 500 characters.",
  "alarmAck.done": "Alarm acknowledged.",
  "alarmAck.by": "Acknowledged by",
  "alarmAck.at": "Acknowledged at",
  "alarmAck.note": "Note",
  "alarmAck.noNote": "No note",
  "alarmAck.waiting": "This alarm hasn't been acknowledged yet.",
  "alarmAck.needsRole": "Alarms are acknowledged by safety officers and above.",

  "map.detail.openAlarm": "Open alarm",

  "day.short.1": "Mon",
  "day.short.2": "Tue",
  "day.short.3": "Wed",
  "day.short.4": "Thu",
  "day.short.5": "Fri",
  "day.short.6": "Sat",
  "day.short.7": "Sun",
  "day.long.1": "Monday",
  "day.long.2": "Tuesday",
  "day.long.3": "Wednesday",
  "day.long.4": "Thursday",
  "day.long.5": "Friday",
  "day.long.6": "Saturday",
  "day.long.7": "Sunday",

  "siteAlerts.title": "Notification settings",
  "siteAlerts.loadError": "Could not load the notification settings.",
  "siteAlerts.readOnly": "Fleet managers and company admins change these settings.",
  "siteAlerts.inherited": "Inherited from parent site: {site}",
  "siteAlerts.own": "Set for this site",
  "siteAlerts.hours.title": "Working hours",
  "siteAlerts.hours.summary": "{days} · {start}–{end}",
  "siteAlerts.hours.summaryAllDay": "{days} · all day",
  "siteAlerts.hours.allDay": "All day",
  "siteAlerts.hours.allDayHint": "00:00–24:00. With every day selected as well, alarms are never outside working hours.",
  "siteAlerts.hours.default": "Default (Mon–Fri 08:00–18:00)",
  "siteAlerts.hours.tz": "Time zone: Turkey (Istanbul). Public holidays are not taken into account yet.",
  "siteAlerts.hours.edit": "Edit working hours",
  "siteAlerts.hours.override": "Set separate hours for this site",
  "siteAlerts.hours.resetParent": "Use the parent site's hours",
  "siteAlerts.hours.resetDefault": "Use the default hours",
  "siteAlerts.hours.resetTitle": "Remove this site's own hours?",
  "siteAlerts.hours.resetBody": "The working hours set for {site} will be removed. The site will then use its parent site's hours, or the defaults if there are none.",
  "siteAlerts.hours.dialogTitle": "Working hours · {site}",
  "siteAlerts.hours.days": "Working days",
  "siteAlerts.hours.start": "Start time",
  "siteAlerts.hours.end": "End time",
  "siteAlerts.hours.daysRequired": "Choose at least one day.",
  "siteAlerts.hours.endAfterStart": "The end time must be after the start time.",
  "siteAlerts.hours.childNote": "Sub-sites without their own hours use these too.",
  "siteAlerts.night.title": "Night contacts",
  "siteAlerts.night.hint": "Impacts outside working hours are e-mailed to these people straight away.",
  "siteAlerts.night.none": "Not set: off-hours impacts go to the daytime recipients",
  "siteAlerts.night.edit": "Edit night contacts",
  "siteAlerts.night.reset": "Remove this site's own list",
  "siteAlerts.night.resetTitle": "Remove this site's own night contacts?",
  "siteAlerts.night.resetBody": "{site} will then use its parent site's night contacts. If no parent site has any, off-hours impacts go to the daytime recipients.",
  "siteAlerts.night.dialogTitle": "Night contacts · {site}",
  "siteAlerts.night.pick": "Users who can see this site",
  "siteAlerts.night.pickHint": "If you choose nobody, the site uses its parent site's night contacts.",
  "siteAlerts.inheritedHidden": "Inherited from a parent site",
  "siteAlerts.night.noCandidates": "There are no users to choose from.",
  "siteAlerts.preview.title": "If something happened now",
  "siteAlerts.preview.inHours": "It's working time now ({time}).",
  "siteAlerts.preview.offHours": "It's outside working hours now ({time}).",
  "siteAlerts.preview.impactNow": "An impact is e-mailed straight away to: {who}.",
  "siteAlerts.preview.othersNow": "Other alarms also go to the daytime recipients straight away.",
  "siteAlerts.preview.impactFallback": "No night contacts are set, so impacts go to the daytime recipients.",
  "siteAlerts.preview.impactDigest": "Daytime recipients who were not e-mailed at once see the impact in the morning digest on {when}.",
  "siteAlerts.preview.othersDigest": "Other alarms arrive in the morning digest on {when}: {who}.",
  "siteAlerts.preview.noDigest": "No working days are selected, so no morning digest is sent.",
  "siteAlerts.preview.dayGroup": "{count} people with the safety officer or fleet manager role",
  "siteAlerts.preview.dayGroupOne": "1 person with the safety officer or fleet manager role",
  "siteAlerts.preview.adminsFallback": "{count} company admins",
  "siteAlerts.preview.adminsFallbackOne": "1 company admin",
  "siteAlerts.preview.dayGroupWithAdmins": "{count} people with the safety officer, fleet manager or admin role",
  "siteAlerts.preview.dayGroupWithAdminsOne": "1 person with the safety officer, fleet manager or admin role",
  "siteAlerts.preview.impactAdminsToo": "The impact also goes to {count} company admins at the same time.",
  "siteAlerts.preview.impactAdminsTooOne": "The impact also goes to 1 company admin at the same time.",
  "siteAlerts.preview.adminsCopy": "Company admins also get a copy of every alarm (setting on the Users and roles screen).",
  "siteAlerts.preview.noRecipients": "Nobody at this site is set up to receive notifications; an impact would reach no one.",
  "siteAlerts.preview.impactNobody": "An impact would not be e-mailed to anyone.",
  "siteAlerts.preview.othersNobody": "Other alarms would not reach anyone either.",
  "siteAlerts.preview.othersDigestNobody": "The morning digest on {when} for other alarms would reach no one.",
  "error.device_offline": "The device is offline. Live watch can be started once it reconnects.",
  "error.too_many_live_watches": "At most 5 vehicles can be on live watch at the same time. Try again when one ends.",
  "liveWatch.waiting": "Waiting for the device",
  "liveWatch.waitingLabel": "Live watch sent, waiting for the device to confirm",
  "liveWatch.start": "Live watch (10 min)",
  "liveWatch.starting": "Starting…",
  "liveWatch.active": "Live · {time} left",
  "liveWatch.activeLabel": "Live watch on, {time} left",
  "liveWatch.tooSoon": "Live watch was just requested for this vehicle. Try again in 30 seconds.",
  "liveWatch.hint": "The vehicle reports its position every second for 10 minutes, then goes back to its normal interval. It has no effect on how the vehicle runs.",

  "geofence.title": "Zones",
  "geofence.add": "Add zone",
  "geofence.intro": "Define no-go zones, work areas or time limits; when a rule is broken an alarm is raised and sent according to the notification rules.",
  "geofence.empty": "No zones are defined for this site.",
  "geofence.emptyEditable": "No zones yet. Draw no-go zones such as pedestrian walkways, or work areas, on the map.",
  "geofence.loadError": "Could not load the zones.",
  "geofence.tableCaption": "Zones of {site}",
  "geofence.name": "Name",
  "geofence.nameHint": "Shown on the map and in alarms, e.g. Pedestrian walkway – main gate.",
  "geofence.kind": "Type",
  "geofence.shape": "Shape",
  "geofence.vehicleTypes": "Vehicle types",
  "geofence.status": "Status",
  "geofence.actions": "Actions",
  "geofence.active": "Active",
  "geofence.inactive": "Inactive",
  "geofence.editNamed": "Edit zone {name}",
  "geofence.deleteNamed": "Delete zone {name}",
  "geofence.readOnly": "Zones are managed by safety officers, fleet managers and company admins.",
  "geofence.saved": "{name} saved.",
  "geofence.deleted": "{name} deleted.",
  "geofence.limitReached": "A site can have at most {max} zones.",
  "geofence.kind.forbidden": "No-go zone",
  "geofence.kind.work_area": "Work area",
  "geofence.kind.dwell": "Time limit",
  "geofence.kindHint.forbidden": "An alarm is raised when a vehicle enters. For pedestrian walkways and narrow passages.",
  "geofence.kindHint.work_area": "An alarm is raised when a vehicle leaves the area.",
  "geofence.kindHint.dwell": "An alarm is raised when a vehicle stays in the zone longer than the minutes you set.",
  "geofence.rule.forbidden": "Alarm when a vehicle enters",
  "geofence.rule.work_area": "Alarm when a vehicle leaves",
  "geofence.rule.dwell": "Alarm when a vehicle stays longer than {duration}",
  "geofence.minutes": "{m} min",
  "geofence.hours": "{h} h",
  "geofence.hoursMinutes": "{h} h {m} min",
  "geofence.shapePolygonSummary": "Polygon · {count} points",
  "geofence.shapeCircleSummary": "Circle · {radius} m",
  "geofence.allVehicleTypes": "All vehicles",
  "geofence.addTitle": "New zone",
  "geofence.editTitle": "Edit zone",
  "geofence.dialogHint": "Site: {site}",
  "geofence.dwellMinutes": "Time limit (minutes)",
  "geofence.dwellHint": "{min}–{max} minutes. An alarm is raised when a vehicle stays in the zone longer than {minutes} minutes, once per visit.",
  "geofence.shape.polygon": "Polygon",
  "geofence.shape.circle": "Circle",
  "geofence.drawPolygonHint": "Click the map to add points; drag points to move them. With the keyboard: pan the map with the arrow keys and press \"Add point at map centre\"; a focused point moves 1 m with the arrow keys (5 m with Shift) and Delete removes it.",
  "geofence.drawCircleHint": "Click the map or drag the centre to place the circle, then enter the radius.",
  "geofence.mapError": "The map could not be loaded. Check your connection and try again.",
  "geofence.mapLabel": "Zone drawing map",
  "geofence.vertexLabel": "Point {n}",
  "geofence.centerLabel": "Circle centre",
  "geofence.vertexCount": "{count} points",
  "geofence.addVertexAtCenter": "Add point at map centre",
  "geofence.undoVertex": "Undo last point",
  "geofence.clearVertices": "Clear",
  "geofence.centerSet": "Centre placed",
  "geofence.centerMissing": "No centre yet",
  "geofence.centerAtMapCenter": "Place centre at map centre",
  "geofence.radius": "Radius (m)",
  "geofence.radiusHint": "{min}–{max} metres.",
  "geofence.vehicleTypesHint": "Leave all unticked to apply the zone to every vehicle.",
  "geofence.activeLabel": "Active",
  "geofence.activeHint": "An inactive zone raises no alarms but is kept.",
  "geofence.gnssNote": "Zones are checked with outdoor satellite (GNSS) positions only; nothing is raised indoors. To avoid flicker at the edge, the state changes only once a vehicle has been at least 3 m across the boundary for 5 seconds and 5 positions in a row, which is why zones must be at least 10 m wide. A time-limit alarm needs a position from inside the zone in the last 5 minutes. An alarm is only a notification; the vehicle is never stopped.",
  "geofence.deleteTitle": "Delete this zone?",
  "geofence.deleteBody": "{name} will be deleted. Alarms already raised for it are kept, but the zone's shape will no longer be shown.",
  "geofence.issue.name_required": "Give the zone a name.",
  "geofence.issue.name_too_long": "The name can be at most 80 characters.",
  "geofence.issue.invalid_kind": "Choose a type.",
  "geofence.issue.invalid_shape": "Choose polygon or circle.",
  "geofence.issue.invalid_coordinate": "The zone's position is missing or invalid. Draw it again on the map.",
  "geofence.issue.too_few_vertices": "Mark at least 3 points on the map for a polygon.",
  "geofence.issue.too_many_vertices": "A polygon can have at most 200 points.",
  "geofence.issue.self_intersecting": "The polygon's edges cross each other. Move the points to fix it.",
  "geofence.issue.too_narrow": "A zone must be at least 10 m wide. GPS can be off by 2–5 m on site, so narrower areas (such as walkways) cannot be watched reliably; for those, UWB or camera-based solutions are recommended.",
  "geofence.issue.radius_out_of_range": "The radius can be at most 5,000 metres.",
  "geofence.issue.dwell_out_of_range": "The time limit must be a whole number between 1 and 1,440 minutes.",
  "geofence.issue.dwell_only_for_dwell": "A time limit only applies to time-limit zones.",
  "geofence.issue.invalid_vehicle_type": "An unknown vehicle type was selected.",
  "alarmZone.title": "Zone",
  "alarmZone.zone": "Zone",
  "alarmZone.deleted": "Zone deleted",
  "alarmZone.unavailable": "Zone not available",
  "alarmZone.kind": "Rule",
  "alarmZone.point": "Where it happened",
  "alarmZone.noPoint": "No position (raised by the minute check while the vehicle stayed in the zone)",

  "alarmZone.mapLabel": "Map of the zone and where it happened",
  "alarmZone.pointPin": "Where it happened",
  "alarmZone.noBlock": "The vehicle is not stopped; a zone alarm is only a notification.",
  "map.zones.toggle": "Show zones",
  "map.zones.legend": "Zone types",
  "error.invalid_geofence": "The zone could not be saved: its shape, name or time limit breaks a rule.",
  "error.too_many_geofences": "This site has reached the zone limit (50).",

  /* Sprint 7a: pre-shift checklist (ADR 0016) */
  "error.checklist_token_invalid": "This QR code is invalid or has been replaced.",
  "error.invalid_checklist": "The checklist is incomplete or invalid.",
  "error.invalid_photo": "The photo was not accepted: it must be JPEG, PNG or WebP and at most 5 MB.",
  "error.invalid_template": "The template could not be saved: a name, item code or text breaks a rule.",
  "error.invalid_shift": "The shift could not be saved: the name, times or days are invalid.",
  "error.too_many_shifts": "This site has reached the shift limit (10).",
  "error.invalid_recipient": "The recipient could not be added: the person cannot see this site, the address is invalid or already listed.",
  "error.fault_resolved": "This fault has already been marked as fixed.",
  "alarm.type.checklist_critical": "Checklist: critical fault",
  "alarm.type.checklist_missed": "Checklist not completed",
  "alarm.type.checklist_notes": "Checklist: fault reported",
  "alarmKind.maintenance_report": "Maintenance report (external address)",
  "alarmNotify.external": "External address",
  "alarmChecklist.title": "Checklist",
  "alarmChecklist.shift": "Shift",
  "alarmChecklist.open": "Open the vehicle's checklists",
  "alarmChecklist.missedText": "The vehicle was used in this shift without a pre-shift checklist.",
  "alarmChecklist.noLock": "The vehicle is not locked; this is a notification only.",
  "nav.group.checklist": "Checklist",
  "nav.checklistFaults": "Faults",
  "nav.checklistTemplates": "Templates",
  "checklist.category.counterbalance_electric": "Electric counterbalance",
  "checklist.category.counterbalance_ic": "Diesel/LPG counterbalance",
  "checklist.category.reach_truck": "Reach truck",
  "checklist.category.order_picker": "Order picker",
  "checklist.category.pallet_truck": "Electric pallet truck",
  "checklist.systemName": "{category} (standard)",
  "checklist.result.ok": "OK",
  "checklist.result.ok_with_notes": "OK with notes",
  "checklist.result.maintenance_required": "Maintenance required",
  "checklist.answer.ok": "OK",
  "checklist.answer.not_ok": "Not OK",
  "checklist.answer.na": "N/A",
  "checklist.critical": "Critical",
  "checklist.photoRequired": "Photo",
  "cf.title": "Pre-shift check",
  "cf.invalid": "This QR code is invalid or has been replaced. Please tell your supervisor.",
  "cf.rateLimited": "Too many attempts. Please try again in a few minutes.",
  "cf.loadError": "The form could not be loaded. Check your connection and try again.",
  "cf.shift": "Shift: {name}",
  "cf.noShift": "Outside defined shifts",
  "cf.alreadySubmitted": "A checklist was already filled in for this shift. You can fill it in again if needed.",
  "cf.openFaults": "Open faults (from earlier checks)",
  "cf.intro": "Check and mark every item. Choose \"N/A\" for equipment the vehicle does not have.",
  "cf.note": "Note",
  "cf.notePlaceholder": "Briefly describe what you saw",
  "cf.addPhoto": "Add photo",
  "cf.photoUploading": "Uploading photo…",
  "cf.photoRemove": "Remove photo",
  "cf.photoAlt": "Photo {n} of item {code}",
  "cf.photoCount": "{n}/3 photos",
  "cf.progress": "{done}/{total} items",
  "cf.submit": "Submit",
  "cf.submitting": "Submitting…",
  "cf.missing": "{n} item(s) incomplete. Complete the marked items.",
  "cf.done": "Thank you, the checklist has been saved.",
  "cf.doneDetail": "{fleetNo} · {time}",
  "cf.again": "Fill in a new checklist",
  "cf.waitPhotos": "Photos are still uploading; tap \"Submit\" again when they are done.",
  "error.form_changed": "The form was updated, please reload it.",
  "cf.formChanged": "The form was updated, please reload it. Your answers to unchanged items are kept.",
  "cf.reload": "Reload the form",
  "cf.naNotAllowed": "\"N/A\" is not available for this item",
  "cf.issue.na_not_allowed": "\"N/A\" is not allowed for this item; choose \"OK\" or \"Not OK\".",
  "templateEdit.allowNa": "Allow \"N/A\" even though critical (conditional equipment)",
  "checklist.allowNa": "N/A allowed",
  "cf.language": "Language",
  "cf.issue.missing_answer": "Choose an option.",
  "cf.issue.duplicate_answer": "This item was answered more than once.",
  "cf.issue.unknown_item": "This item is no longer in the template. Reload the form.",
  "cf.issue.invalid_answer": "Invalid answer.",
  "cf.issue.note_required": "Write a note for \"Not OK\".",
  "cf.issue.note_too_long": "The note can be at most 500 characters.",
  "cf.issue.photo_required": "Add a photo for this item.",
  "cf.issue.too_many_photos": "At most 3 photos can be added.",
  "vehicleDetail.tab.checklist": "Checklist",
  "vehicleChecklist.maintenance": "Maintenance required",
  "vehicleChecklist.maintenanceHint": "There is an open critical fault. The vehicle is not locked; mark the fault when it is fixed.",
  "vehicleChecklist.openFaults": "Open faults",
  "vehicleChecklist.noOpenFaults": "No open faults.",
  "vehicleChecklist.history": "Checklist history",
  "vehicleChecklist.empty": "No checklist has been filled in for this vehicle yet.",
  "vehicleChecklist.when": "When",
  "vehicleChecklist.shift": "Shift",
  "vehicleChecklist.operator": "Operator",
  "vehicleChecklist.operatorUnknown": "Unknown (no card session)",
  "vehicleChecklist.result": "Result",
  "vehicleChecklist.template": "Template",
  "vehicleChecklist.details": "Details",
  "vehicleChecklist.hideDetails": "Hide details",
  "vehicleChecklist.impact": "There was an impact alarm since the previous check",
  "vehicleChecklist.loadError": "Checklists could not be loaded.",
  "vehicleChecklist.photos": "Photos",
  "checklistQr.title": "QR label",
  "checklistQr.hint": "Stick the label where the operator sees it. The QR code opens the form without a console login.",
  "checklistQr.print": "Print label",
  "checklistQr.printAll": "Print label sheet",
  "checklistQr.rotate": "Replace QR code",
  "checklistQr.rotateTitle": "Replace the QR code?",
  "checklistQr.rotateText": "The old label stops working at once. Print the new label and replace the old one.",
  "checklistQr.noAccess": "Fleet managers and above can see the QR code.",
  "checklistQr.labelTitle": "Pre-shift check",
  "checklistQr.labelText": "At the start of the shift, scan this code with your phone and fill in the form (about 3 minutes).",
  "checklistQr.labelSample": "Sample label text — adapt it to your site.",
  "checklistQr.link": "Form link",
  "checklistQr.copy": "Copy link",
  "checklistQr.sheetTitle": "QR labels · {site}",
  "checklistQr.close": "Close",
  "faults.title": "Faults",
  "faults.subtitle": "Items marked \"Not OK\" in checklists",
  "faults.filter.open": "Open",
  "faults.filter.resolved": "Fixed",
  "faults.filter.all": "All",
  "faults.filterLabel": "Filter by status",
  "faults.vehicle": "Vehicle",
  "faults.item": "Item",
  "faults.note": "Note",
  "faults.reported": "Reported",
  "faults.status": "Status",
  "faults.status.open": "Open",
  "faults.status.resolved": "Fixed",
  "faults.resolve": "Mark fixed",
  "faults.resolveTitle": "Has the fault been fixed?",
  "faults.resolveNote": "What was done",
  "faults.resolveHint": "E.g. tyre replaced. At most 500 characters.",
  "faults.resolvedBy": "{name} · {time}",
  "faults.empty": "No faults with this filter.",
  "faults.needsRole": "Fleet managers and above close faults.",
  "faults.photos": "{n} photo(s)",
  "templates.title": "Checklist templates",
  "templates.subtitle": "Copy a standard template, adjust its items and assign it to vehicle types.",
  "templates.system": "Standard",
  "templates.own": "Company template",
  "templates.items": "{n} items",
  "templates.copy": "Copy",
  "templates.copyName": "{name} (copy)",
  "templates.view": "View",
  "templates.delete": "Delete",
  "templates.deleteTitle": "Delete the template?",
  "templates.deleteText": "Vehicle types it is assigned to go back to the standard template. Filled-in checklists do not change.",
  "templates.assignTitle": "Assignment to vehicle types",
  "templates.assignHint": "Each vehicle type is checked with the template chosen here. Without a choice the standard template is used.",
  "templates.category": "Vehicle type",
  "templates.template": "Template",
  "templates.useSystem": "Standard template",
  "templates.viewOnly": "Templates are edited by fleet managers without a site restriction and company admins.",
  "templates.critical": "{n} critical",
  "templateEdit.title": "Edit template",
  "templateEdit.viewTitle": "Template items",
  "templateEdit.name": "Template name",
  "templateEdit.code": "Code",
  "templateEdit.textTr": "Item (Turkish)",
  "templateEdit.textEn": "Item (English)",
  "templateEdit.critical": "Critical (notify at once when not OK)",
  "templateEdit.photo": "Photo required when not OK",
  "templateEdit.impactFirst": "Show first after an impact",
  "templateEdit.lpgOnly": "LPG vehicles only",
  "templateEdit.moveUp": "Move up",
  "templateEdit.moveDown": "Move down",
  "templateEdit.remove": "Remove item",
  "templateEdit.add": "Add item",
  "templateEdit.itemLabel": "Item {n}",
  "templateEdit.noLock": "Critical items never lock the vehicle; they only notify the chosen people at once.",
  "templateEdit.issue.name_required": "Enter a template name.",
  "templateEdit.issue.name_too_long": "The template name can be at most 80 characters.",
  "templateEdit.issue.no_items": "Add at least one item.",
  "templateEdit.issue.too_many_items": "A template can have at most 60 items.",
  "templateEdit.issue.text_required": "Enter the item text (Turkish).",
  "templateEdit.issue.text_too_long": "The item text can be at most 200 characters.",
  "templateEdit.issue.invalid_code": "Code: 1–8 characters, capital letters, digits or dashes.",
  "templateEdit.issue.duplicate_code": "This code is used by another item.",
  "siteChecklist.title": "Pre-shift checklist",
  "siteChecklist.shiftsTitle": "Shifts",
  "siteChecklist.shiftsHint": "A checklist is required at every shift start. A vehicle used in a shift without a checklist raises an alarm after 15 minutes.",
  "siteChecklist.inherited": "Inherited from a parent site: {site}",
  "siteChecklist.inheritedHidden": "Inherited from a parent site",
  "siteChecklist.noShifts": "No shifts defined: no \"checklist not completed\" alarms are raised.",
  "siteChecklist.addShift": "Add shift",
  "siteChecklist.editShift": "Edit shift",
  "siteChecklist.shiftName": "Shift name",
  "siteChecklist.start": "Start",
  "siteChecklist.end": "End",
  "siteChecklist.days": "Days (start day)",
  "siteChecklist.timeZone": "Time zone",
  "siteChecklist.overnight": "next day {time}",
  "siteChecklist.deleteShift": "Delete shift",
  "siteChecklist.deleteShiftText": "Delete the \"{name}\" shift? Past checklists do not change.",
  "siteChecklist.recipientsTitle": "Checklist notifications",
  "siteChecklist.recipientsHint": "Critical faults and missed checklists go to these people at once; non-critical faults go to the daily summary. The operator sees no warning and the vehicle is not locked.",
  "siteChecklist.noRecipients": "No recipients: notifications go to the site's safety officer and fleet manager.",
  "siteChecklist.addMember": "Add console user",
  "siteChecklist.addExternal": "Add external e-mail",
  "siteChecklist.member": "User",
  "siteChecklist.email": "E-mail address",
  "siteChecklist.label": "Description",
  "siteChecklist.labelHint": "E.g. the name of the outside maintenance company.",
  "siteChecklist.externalHint": "External addresses receive the vehicle, failed items, notes and photo links valid for 7 days; the operator's name is never sent (KVKK).",
  "siteChecklist.external": "External address",
  "siteChecklist.remove": "Remove",
  "siteChecklist.removeTitle": "Remove the recipient?",
  "siteChecklist.removeText": "{name} will no longer receive this site's checklist notifications.",
  "siteChecklist.externalAdminOnly": "Only company admins add or delete external e-mail addresses.",
  "siteChecklist.loadError": "Checklist settings could not be loaded.",
  "siteChecklist.ownList": "This site's list",
  "siteChecklist.applies": "Recipients that apply now",
};
